import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, User2, Building2, MessageSquare, ArrowRight, CheckCircle2, HeadphonesIcon } from "lucide-react";

const OGGETTI = [
  "Iperammortamento 2026 (L. 199/2025)",
  "Nuova Sabatini",
  "Bando specifico",
  "Preventivo per la mia azienda",
  "Altro",
];

export default function Contattaci() {
  const [form, setForm] = useState({
    nome: "",
    azienda: "",
    email: "",
    telefono: "",
    oggetto: OGGETTI[0],
    messaggio: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.messaggio.trim()) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, origine: "contattaci" });
      setSent(true);
      toast.success("Richiesta inviata. Ti contatteremo entro 24 ore lavorative.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="contattaci-page">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        {/* Left: intro */}
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand/10 text-brand rounded-full text-[11px] font-display uppercase tracking-[0.2em]">
            <HeadphonesIcon className="w-3 h-3" /> Parla con uno specialista
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl text-ink tracking-tight">
            Domande, dubbi o <span className="text-brand">un caso complesso</span>?
          </h1>
          <p className="mt-4 text-ink-muted leading-relaxed">
            Compila il form: un nostro consulente ti contatta <strong className="text-ink">entro 24 ore lavorative</strong> per una prima consulenza gratuita. Ricordati che lavoriamo a success fee: <strong>ci paghi solo se la pratica viene approvata dal GSE</strong>.
          </p>
          <div className="mt-8 space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-brand/10 text-brand flex items-center justify-center flex-shrink-0"><Mail className="w-4 h-4" /></div>
              <div>
                <div className="text-ink-muted text-[11px] font-mono uppercase tracking-widest">Email</div>
                <a href="mailto:info@studioingegneriavalore.it" className="text-ink hover:text-brand">info@studioingegneriavalore.it</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-brand/10 text-brand flex items-center justify-center flex-shrink-0"><Phone className="w-4 h-4" /></div>
              <div>
                <div className="text-ink-muted text-[11px] font-mono uppercase tracking-widest">Telefono</div>
                <a href="tel:+390455116212" className="text-ink hover:text-brand">045 5116212</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-brand/10 text-brand flex items-center justify-center flex-shrink-0"><Building2 className="w-4 h-4" /></div>
              <div>
                <div className="text-ink-muted text-[11px] font-mono uppercase tracking-widest">Studio Ingegneria Valore</div>
                <div className="text-ink">Via Ca&apos; Brusà 16 · 37030 Lavagno (VR)</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-3">
          {sent ? (
            <div className="bg-white border border-line rounded-lg shadow-card p-10 text-center" data-testid="contattaci-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-brand/10 text-brand flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h2 className="mt-4 font-display text-2xl text-ink">Richiesta ricevuta</h2>
              <p className="mt-2 text-ink-muted">
                Grazie, {form.nome}. Un nostro specialista ti contatta a breve all&apos;indirizzo <strong className="text-ink">{form.email}</strong>.
              </p>
              <p className="mt-4 text-xs text-ink-muted">
                Controlla anche la cartella <strong>Spam</strong> dove abbiamo appena inviato una copia della richiesta.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-line rounded-lg shadow-card p-6 md:p-8" data-testid="contattaci-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Nome e cognome *" icon={<User2 className="w-4 h-4" />}>
                  <input required value={form.nome} onChange={(e) => upd("nome", e.target.value)} data-testid="lead-nome" className="input-base" />
                </Field>
                <Field label="Ragione sociale" icon={<Building2 className="w-4 h-4" />}>
                  <input value={form.azienda} onChange={(e) => upd("azienda", e.target.value)} data-testid="lead-azienda" className="input-base" />
                </Field>
                <Field label="Email *" icon={<Mail className="w-4 h-4" />}>
                  <input required type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="lead-email" className="input-base" />
                </Field>
                <Field label="Telefono" icon={<Phone className="w-4 h-4" />}>
                  <input value={form.telefono} onChange={(e) => upd("telefono", e.target.value)} data-testid="lead-telefono" className="input-base" />
                </Field>
              </div>
              <label className="block mt-4">
                <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">Oggetto</span>
                <select value={form.oggetto} onChange={(e) => upd("oggetto", e.target.value)} data-testid="lead-oggetto" className="input-base">
                  {OGGETTI.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
              <label className="block mt-4">
                <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">Messaggio *</span>
                <div className="relative">
                  <MessageSquare className="w-4 h-4 text-ink-muted absolute left-3 top-3" />
                  <textarea
                    required rows={5}
                    value={form.messaggio}
                    onChange={(e) => upd("messaggio", e.target.value)}
                    data-testid="lead-messaggio"
                    placeholder="Raccontaci del tuo progetto o della tua domanda…"
                    className="w-full pl-10 pr-3 py-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm resize-none"
                  />
                </div>
              </label>
              <button
                type="submit" disabled={loading} data-testid="lead-submit"
                className="mt-6 w-full h-12 bg-brand text-white font-display uppercase tracking-widest text-sm rounded-md hover:bg-brand-dark hover:shadow-brand-glow disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? "Invio…" : "Invia richiesta"}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
              <p className="mt-3 text-[11px] text-ink-muted text-center">
                I tuoi dati vengono trattati esclusivamente per riscontrare la tua richiesta. Nessuna newsletter automatica.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">{label}</span>
      <div className="relative">
        {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted">{icon}</span>}
        {children}
      </div>
    </label>
  );
}
